import {createBrowserRouter, Navigate} from "react-router-dom";
import ErrorPage from "../pages/Error";
import Login from "../pages/auth/Login";
import AdminRoot from "../pages/AdminRoot";
import UsersPage from "../pages/users/Users";
import UserDetailPage from "../pages/users/UserDetail";
import EditUserPage from "../pages/users/EditUser";
import UsersRootLayout from "../pages/users/UsersRoot";
import OrganizationsPage from "../pages/organization/Organizations";
import App from "../App";
import DivisionsPage from "../pages/divisions/Divisions";
import DivisionDetailPage from "../pages/divisions/DivisionDetail";
import GroupTeamPage from "../pages/divisions/Groups";
import StageDetailPage from "../pages/stage/StageDetail";
import TeamDetailPage from "../pages/team/TeamDetail";
import Register from "../pages/auth/Register";
import TermsAndPolicy from "../pages/Policy";
import VerifyEmail from "../pages/users/VerifyEmail";
import ResendActivation from "../pages/auth/ResendActivation";
import DeleteAccount from "../pages/auth/DeleteAccount";
import InviteRedirect from "../pages/InviteRedirect";
import ListingRedirect from "../pages/ListingRedirect";
import SuccessPage from "../pages/subscription/SuccessPage";
import CancelPage from "../pages/subscription/CancelPage";
import PasswordReset from "../pages/users/PasswordReset";
import ForgotPassword from "../pages/users/ForgotPassword";
import SubscriptionPage from "../pages/subscription/SubscriptionPage";
import InvoicesPage from "../pages/subscription/InvoicesPage";
import WaiversPage from "../pages/waiver/WaiversPage";
import SanctionRulesPage from "../pages/sanction/SanctionRulesPage";
import {VenuesPage} from "../pages/venue/VenuesPage";
import StripeOnboardSuccessPage from "../pages/stripe/StripeOnboardSuccessPage";
import StripeOnboardRetryPage from "../pages/stripe/StripeOnboardRetryPage";
import StripeAccountsPage from "../pages/stripe/StripeAccountsScreen";
import PublicEventPage from "../pages/event/PublicEventPage";
import FinancialDashboardPage from "../pages/financial/FinancialDashboard";
import Advertise from "../pages/sponsor/Advertise";
import AdvertisingDashboard from "../pages/sponsor/AdvertisingDashboard";
import Audience from "../pages/sponsor/Audience";
import ManagementData from "../pages/sponsor/ManagementData";
import Sponsors from "../pages/sponsor/Sponsors";
import Campaigns from "../pages/sponsor/Campaigns";
import CreateCampaign from "../pages/sponsor/CreateCampaign";
import EditCampaign from "../pages/sponsor/EditCampaign";
import CampaignReview from "../pages/sponsor/CampaignReview";
import CheckoutSuccess from "../pages/sponsor/CheckoutSuccess";
import CheckoutCancel from "../pages/sponsor/CheckoutCancel";
import RequireAuth from "./RequireAuth";
import NotificationsPage from "../pages/notification/NotificationsPage";
import PublicSchedulePage from "../pages/event/PublicSchedulePage";
import HomeEntry from "../pages/HomeEntry";
import StaffPage from "../pages/staff/StaffPage";
import OrgPortalDivisionPage from "../pages/organization/OrgPortalDivisionPage";
import PlayersPage from "../pages/players/PlayersPage";
import RefereesPage from "../pages/referee/RefereesPage";
import AssignmentsPage from "../pages/referee/AssignmentsPage";
import StreamProvidersPage from "../pages/settings/StreamProvidersPage";
import MarketplaceListingPage from "../pages/marketplace/MarketplaceListingPage";
import MarketplaceManagementPage from "../pages/marketplace/MarketplaceManagementPage";
import MarketplaceListingFormPage from "../pages/marketplace/MarketplaceListingFormPage";
import MarketplaceModerationPage from "../pages/marketplace/MarketplaceModerationPage";

const router = createBrowserRouter([
    {
        path: "/",
        element: <App/>,
        errorElement: <ErrorPage/>,
        children: [
            // PUBLIC ROUTES
            {index: true, element: <HomeEntry/>},
            {path: "login", element: <Login/>},
            {path: "register", element: <Register/>},
            {path: "terms", element: <TermsAndPolicy/>},
            {path: "verify-email/:token", element: <VerifyEmail/>},
            {path: "resend-activation", element: <ResendActivation/>},
            {path: "delete-account", element: <DeleteAccount/>},
            {path: "invite", element: <InviteRedirect/>},
            {path: "listing/:listingSlug", element: <ListingRedirect/>},
            {path: "subscription-success", element: <SuccessPage/>},
            {path: "subscription-cancel", element: <CancelPage/>},
            {path: "forgot-password", element: <ForgotPassword/>},
            {path: "password-reset/:token", element: <PasswordReset/>},
            {path: "stripe-onboard-success", element: <StripeOnboardSuccessPage/>},
            {path: "stripe-onboard-retry", element: <StripeOnboardRetryPage/>},
            {path: "events/:eventId", element: <PublicEventPage/>},
            {path: "schedule/:seasonSlug", element: <PublicSchedulePage/>},
            {path: "advertising", element: <Advertise/>},

            // Organization Portal
            {path: "division/:divisionSlug/:seasonSlug", element: <OrgPortalDivisionPage/>},

            // PRIVATE ROUTES (GUARDED)
            {
                element: <RequireAuth/>,
                children: [
                    {
                        path: "management/organizations",
                        element: <AdminRoot/>,
                        children: [
                            {index: true, element: <OrganizationsPage/>},
                            {path: ":orgSlug/divisions", element: <DivisionsPage/>},
                            {path: ":orgSlug/divisions/:divSlug", element: <DivisionDetailPage/>},
                            {
                                path: ":orgSlug/divisions/:divSlug/schedule/:seasonSlug",
                                element: <GroupTeamPage/>,
                            },
                            {
                                path: ":orgSlug/divisions/:divSlug/stage/:seasonSlug",
                                element: <StageDetailPage/>,
                            },
                            {
                                path: ":orgSlug/divisions/:divSlug/team/:teamSlug",
                                element: <TeamDetailPage/>,
                            },

                            {
                                path: "users",
                                element: <UsersRootLayout/>,
                                children: [
                                    {index: true, element: <UsersPage/>},
                                    {path: ":userId", element: <UserDetailPage/>},
                                    {path: ":userId/edit", element: <EditUserPage/>},
                                ],
                            },

                            // ⬇️ 404 inside organization/admin shell (optional but recommended)
                            {path: "*", element: <ErrorPage/>},
                        ],
                    },
                    {
                        path: "notifications",
                        element: <AdminRoot/>,
                        children: [{index: true, element: <NotificationsPage/>}],
                    },
                    {
                        path: "marketplace",
                        element: <AdminRoot/>,
                        children: [
                            {index: true, element: <MarketplaceManagementPage/>},
                            {path: "new", element: <MarketplaceListingFormPage/>},
                            {path: ":id/edit", element: <MarketplaceListingFormPage/>},
                            {path: ":id", element: <MarketplaceListingPage/>},
                        ],
                    },
                    {
                        path: "management/players",
                        element: <AdminRoot/>,
                        children: [{index: true, element: <PlayersPage/>}],
                    },
                    {
                        path: "management",
                        element: <AdminRoot/>,
                        children: [
                            {path: "referees", element: <RefereesPage/>},
                            {path: "assignments", element: <AssignmentsPage/>},
                            {path: "staff", element: <StaffPage/>},
                            {path: "venues", element: <VenuesPage/>},
                        ],
                    },
                    {
                        path: "officials",
                        element: <AdminRoot/>,
                        children: [
                            {path: "referees", element: <Navigate replace to="/management/referees"/>},
                            {path: "assignments", element: <Navigate replace to="/management/assignments"/>},
                        ],
                    },
                    {
                        path: "settings",
                        element: <AdminRoot/>,
                        children: [
                            {path: "stream-providers", element: <StreamProvidersPage/>},
                            {path: "waivers", element: <WaiversPage/>},
                            {path: "sanction-rules", element: <SanctionRulesPage/>},
                            {path: "bank-accounts", element: <StripeAccountsPage/>},
                            {path: "venues", element: <Navigate replace to="/management/venues"/>},
                            {path: "staff", element: <Navigate replace to="/management/staff"/>},
                            {path: "players", element: <Navigate replace to="/management/players"/>},
                            {path: "referees", element: <Navigate replace to="/management/referees"/>},
                        ],
                    },

                    {
                        path: "management/financial",
                        element: <AdminRoot/>,
                        children: [{index: true, element: <FinancialDashboardPage/>}],
                    },

                    {
                        path: "subscription",
                        element: <AdminRoot/>,
                        children: [
                            {path: "plans", element: <SubscriptionPage/>},
                            {path: "invoices", element: <InvoicesPage/>},
                        ],
                    },

                    {
                        path: "advertise",
                        element: <AdminRoot/>,
                        children: [
                            {path: "dashboard", element: <AdvertisingDashboard/>},
                            {path: "audience", element: <Audience/>},
                            {path: "sponsor", element: <Sponsors/>},
                            {path: "campaign", element: <Campaigns/>},
                            {path: "campaign/new", element: <CreateCampaign/>},
                            {path: "campaign/:id/edit", element: <EditCampaign/>},
                            {path: 'campaign/:id/review', element: <CampaignReview/>},
                            {path: 'campaign/checkout/success', element: <CheckoutSuccess/>},
                            {path: 'campaign/checkout/cancel', element: <CheckoutCancel/>},
                        ],
                    },
                    {
                        path: "admin",
                        element: <AdminRoot/>,
                        children: [
                            {path: "management-data", element: <ManagementData/>},
                            {path: "marketplace", element: <MarketplaceModerationPage/>},
                        ],
                    },
                ],
            },

            // ⬇️ Root catch-all: any unknown route under "/" shows ErrorPage inside App shell
            {path: "*", element: <ErrorPage/>},
        ],
    },
]);

export default router;
